
/* eslint-disable no-use-before-define */
import { defineComponent, reactive, ref, Ref, watch, getCurrentInstance, computed } from 'vue';
import {
  postWebV1CertificateInfoPageApi,
  postWebV1CertificateInfoApi,
  putWebV1CertificateInfoApi,
  postWebV1CertificateInfoUpdateEnableApi,
  getWebV1CertificateLogCidApi,
} from '@/api/manage-app/certificate-info';
import {
  PostWebV1CertificateInfoPageApiResListResItem,
  PostWebV1CertificateInfoPageApiReq,
  PostWebV1CertificateInfoApiReq,
  GetWebV1CertificateLogCidApiResItem,
} from '@/api/manage-app/certificate-info/index.d';
import { useList } from '@/hooks/use-list';
import { ElMessage } from 'element-plus';
import { IFormData, IStopFormData } from './type.d';
import { formatTime } from '@toy/utils';
import usePermissionConfig from './use-permission-config';
import { CirclePlus, Remove } from '@element-plus/icons-vue';

export default defineComponent({
  name: 'NotificationManage',
  components: {
    CirclePlus,
    Remove,
  },
  setup() {
    const {
      params,
      tableTotal,
      tableLoading,
      tableData,
      handleSearch,
      handleReset,
      handleSizeChange,
      handleCurrentChange,
    } = useList<PostWebV1CertificateInfoPageApiResListResItem, PostWebV1CertificateInfoPageApiReq>({
      response: {
        tableDataKey: 'data.list',
      },
      request: {
        api: postWebV1CertificateInfoPageApi,
        params: {
          dateRange: [],
          creatorName: '',
          startTime: '',
          endTime: '',
          pageNum: 1,
          pageSize: 10,
        },
        handleParams: (paramsObj) => {
          if (
            paramsObj.dateRange
            && paramsObj.dateRange.length === 2
          ) {
            [paramsObj.startTime, paramsObj.endTime] = [
              `${formatTime(paramsObj.dateRange[0], 'YYYY-MM-DD')} 00:00:00`,
              `${formatTime(paramsObj.dateRange[1], 'YYYY-MM-DD')} 23:59:59`,
            ];
          }
          delete paramsObj.dateRange;
          return paramsObj;
        },
      },
    });
    handleSearch();
    const currentVm = getCurrentInstance()!;
    // 创建/编辑数据
    const formData: Ref<IFormData> = ref({
      id: '',
      noticeTime: '',
      noticeType: [],
      emails: [
        {
          email: '',
        },
      ],
      remark: '',
    });
    // 控制是否展示邮箱
    const isShowEmails: Ref<boolean> = ref(false);
    watch(() => formData.value.noticeType, (val: string[]) => {
      isShowEmails.value = val.includes('邮箱');
    }, {
      deep: true,
    });
    // 新建/编辑点击保存
    const handleSubmit = async () => {
      try {
        await (currentVm.refs.certificateForm as any).validate();
        const newParams: PostWebV1CertificateInfoApiReq = {
          noticeTime: formatTime(formData.value.noticeTime),
          noticeType: formData.value.noticeType.join(','),
          emails: formData.value.emails!.map(((item: { email: string; }) => item.email)).join(','),
          remark: formData.value.remark,
        };
        if (!formData.value.noticeType.includes('邮箱')) {
          delete newParams.emails;
        }
        if (formData.value.id) {
          // 编辑
          newParams.id = formData.value.id;
          await putWebV1CertificateInfoApi(newParams);
        } else {
          // 新建
          await postWebV1CertificateInfoApi(newParams);
        }
        ElMessage.success('操作成功');
        handleSearch();
        dialog.certificate.visible = false;
      } catch (e) {
        console.log(e);
      }
    };
    // 添加/删除email
    const handleEmails = (type: 'add' | 'remove', index: number = 0) => {
      if (type === 'add') {
        formData.value.emails!.push({ email: '' });
      } else {
        formData.value.emails!.splice(index, 1);
      }
    };
    // 打开新建/编辑弹窗
    const handleAddOrEdit = (type: 'add' | 'edit', row: any = {}) => {
      dialog.certificate.visible = true;
      dialog.certificate.title = `${type === 'add' ? '创建' : '编辑'}证书配置`;
      if (type === 'edit') {
        const { noticeTime, noticeType, emails, id = '' } = row;
        formData.value = {
          id,
          noticeTime: formatTime(noticeTime),
          noticeType: noticeType.split(','),
          emails: emails.split(',').map((item: string) => ({ email: item })),
          remark: row.remark,
        };
      } else {
        formData.value = {
          id: '',
          noticeTime: '',
          noticeType: [],
          emails: [
            {
              email: '',
            },
          ],
          remark: '',
        };
      }
    };
    // 打开终止弹窗
    const handleStop = (id: string) => {
      dialog.stop.visible = true;
      stopFormData.value.id = id;
    };
    // 终止数据
    const stopFormData: Ref<IStopFormData> = ref({
      id: '',
      remark: '',
    });
    // 终止
    const handleSubmitStop = async () => {
      console.log(currentVm);
      await (currentVm.refs.stopForm as any).validate();
      await postWebV1CertificateInfoUpdateEnableApi({
        id: stopFormData.value.id as string,
        remark: stopFormData.value.remark as string,
      });
      ElMessage.success('操作成功');
      handleSearch();
      dialog.stop.visible = false;
      stopFormData.value.remark = '';
    };
    // 渲染弹窗底部公共按钮
    const renderFooterButton = (param: 'certificate' | 'stop' | 'operate', fn?: () => void) => {
      return (
        <span>
          <el-button
            size="default"
            onClick={() => {
              dialog[param].visible = false;
            }}
          >{param !== 'operate' ? '取消' : '关闭'}</el-button>
          {param !== 'operate' && <el-button
            size="default"
            type="primary"
            onClick={fn}
          >确认</el-button>}
        </span>
      );
    };
    // 弹窗公共配置
    const dialogCommonConfig = (title: string) => ({
      title,
      center: true,
      'close-on-click-modal': false,
      'custom-class': 'el-dialog-inner-scroll',
    });
    // 弹窗渲染数据
    const dialog = reactive({
      certificate: {
        visible: false,
        ...dialogCommonConfig('创建证书配置'),
        footer: renderFooterButton('certificate', handleSubmit),
      },
      stop: {
        visible: false,
        ...dialogCommonConfig('温馨提示'),
        footer: renderFooterButton('stop', handleSubmitStop),
      },
      operate: {
        visible: false,
        ...dialogCommonConfig('操作记录'),
        footer: renderFooterButton('operate'),
      },
    });
    const operationData: Ref<GetWebV1CertificateLogCidApiResItem[]> = ref([]);
    // 打开操作记录弹窗
    const handleOperate = async (id: string) => {
      operationData.value = [];
      try {
        const { data } = await getWebV1CertificateLogCidApi(id);
        operationData.value = data;
        dialog.operate.visible = true;
      } catch (e) {
        console.log(e);
      }
    };
    watch(() => dialog.stop.visible, (val: boolean) => {
      if (!val) {
        stopFormData.value.remark = '';
      }
    });
    const pickerOptions = computed(() => {
      // function getYearMonthDate(time: Date) {
      //   return `${time.getFullYear()}-${time.getMonth()}-${time.getDate()}`;
      // }
      return {
        // selectableRange: (() => {
        //   const currentTime = new Date();
        //   const hour = currentTime.getHours();
        //   const minute = currentTime.getMinutes();
        //   const second = currentTime.getSeconds();
        //   const isToday = getYearMonthDate(currentTime) === getYearMonthDate(
        //     new Date(formData.value.noticeTime),
        //   );
        //   console.log(isToday ? [`${hour}:${minute}:${second} - 23:59:59`]
        //     : ['00:00:00 - 23:59:59']);
        //   return isToday ? [`${hour}:${minute}:${second} - 23:59:59`] : ['00:00:00 - 23:59:59'];
        // })(),
        disabledDate(time: Date) {
          return time.getTime() < Date.now() - 8.64e7;
        },
      };
    });

    const permissionConfig = usePermissionConfig();
    return {
      operationData,
      handleOperate,
      handleStop,
      handleAddOrEdit,
      handleSubmit,
      isShowEmails,
      params,
      formData,
      stopFormData,
      tableTotal,
      tableLoading,
      handleSearch,
      handleReset,
      handleSizeChange,
      handleCurrentChange,
      dialog,
      handleEmails,
      // 日期选项
      pickerOptions,
      formRules: {
        noticeTime: [
          { required: true, trigger: 'blur', message: '请选择通知时间' },
        ],
        noticeType: [
          { required: true, trigger: 'blur', message: '请选择通知类型' },
        ],
        email: [
          {
            required: true,
            message: '请输入邮箱地址',
          },
          {
            type: 'email',
            message: '请输入正确的邮箱地址',
            trigger: 'blur',
          },
        ],
      },
      tableData,
      permissionConfig,
    };
  },
});
